import type { Card } from '../../types'
import { motion } from 'framer-motion'
import { Music } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { getCardPadding, getScaledFontSize, getScaledIconSize } from '../../utils/gridLayout'

interface SpotifyTopCardProps {
  card: Card
  editMode: boolean
  cellSize: number
}

interface Track {
  name: string
  artist: string
  album_art: string | null
  external_url: string | null
}

function getSubdomainFromHostname(hostname: string): string {
  const host = hostname.split(':')[0].toLowerCase()
  if (!host) return ''

  if (host === 'oda.bio' || host === 'www.oda.bio') return ''

  if (host.endsWith('.oda.bio')) {
    const sub = host.slice(0, -'.oda.bio'.length)
    return sub === 'www' ? '' : sub
  }

  return ''
}

export default function SpotifyTopCard({ card, editMode, cellSize }: SpotifyTopCardProps) {
  const { spotifyTimeRange = 'short_term', songCount = 3 } = card.content
  
  const padding = getCardPadding(cellSize)
  const titleSize = getScaledFontSize(cellSize, 14)
  const trackNameSize = getScaledFontSize(cellSize, 13)
  const artistSize = getScaledFontSize(cellSize, 11)
  const iconSize = getScaledIconSize(cellSize, 20)
  const albumArtSize = Math.max(32, (cellSize / 24) * 40)

  const [isConnected, setIsConnected] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [tracks, setTracks] = useState<Track[]>([])
  
  const timeRangeLabels = {
    short_term: 'bu ay',
    medium_term: 'son 6 ay',
    long_term: 'tüm zamanlar'
  }

  const limit = useMemo(() => Math.max(1, Math.min(10, Number(songCount) || 3)), [songCount])

  useEffect(() => {
    let mounted = true

    const fetchTop = async () => {
      setIsLoading(true)
      try {
        const onPublicProfile = !editMode
        const subdomain = onPublicProfile ? getSubdomainFromHostname(window.location.hostname) : ''
        const url = (onPublicProfile && subdomain) ? `/api/public/spotify/top/${subdomain}` : '/api/spotify/top'
        const res = await axios.get(url, {
          params: { time_range: spotifyTimeRange, limit },
        })
        if (!mounted) return
        setIsConnected(true)
        setTracks(res.data?.tracks ?? [])
      } catch (err: any) {
        if (!mounted) return
        const status = err?.response?.status
        if (status === 401 || status === 404) {
          setIsConnected(false)
          setTracks([])
        }
      } finally {
        if (!mounted) return
        setIsLoading(false)
      }
    }

    void fetchTop()
    const intervalId = window.setInterval(fetchTop, 24 * 60 * 60 * 1000)
    return () => {
      mounted = false
      window.clearInterval(intervalId)
    }
  }, [card.id, spotifyTimeRange, limit])

  return (
    <motion.div
      className="w-full h-full theme-card theme-card-hover flex flex-col"
      style={{ padding: `${padding}px` }}
      whileHover={{ borderColor: 'rgba(0,0,0,0.25)' }}
    >
      {!isConnected ? (
        <div className="flex-1 flex flex-col justify-center">
          <p className="font-serif opacity-80" style={{ fontSize: `${titleSize}px` }}>Spotify bağlı değil</p>
          {editMode && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                window.location.href = '/auth/spotify/login'
              }}
              className="font-serif font-bold border border-[var(--surface-border)] bg-[var(--app-bg)] text-[var(--app-fg)]"
              style={{ marginTop: `${padding}px`, padding: `${padding * 0.5}px ${padding}px`, fontSize: `${titleSize}px` }}
            >
              Connect Spotify
            </button>
          )}
        </div>
      ) : (
      <h3 className="font-serif" style={{ fontSize: `${titleSize}px`, marginBottom: `${padding * 0.75}px` }}>
        {timeRangeLabels[spotifyTimeRange]} en çok dinlenen {songCount} şarkı
      </h3>
      )}

      {isConnected && (
        <div className="flex-1 flex flex-col overflow-hidden" style={{ gap: `${padding * 0.5}px` }}>
          {isLoading ? (
            <p className="font-serif opacity-70" style={{ fontSize: `${titleSize}px` }}>Loading…</p>
          ) : (
            tracks.map((track, index) => (
              <a
                key={index}
                href={track.external_url ?? undefined}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center"
                style={{ gap: `${padding * 0.5}px` }}
              >
                <div className="bg-[var(--app-bg)]/10 flex items-center justify-center flex-shrink-0" style={{ width: `${albumArtSize}px`, height: `${albumArtSize}px` }}>
                  {track.album_art ? (
                    <img src={track.album_art ?? undefined} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Music size={iconSize * 0.8} className="opacity-70" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif font-bold truncate" style={{ fontSize: `${trackNameSize}px` }}>{track.name}</p>
                  <p className="font-serif truncate opacity-70" style={{ fontSize: `${artistSize}px` }}>{track.artist}</p>
                </div>
              </a>
            ))
          )}
        </div>
      )}
    </motion.div>
  )
}
